import { useQuery } from '@tanstack/react-query'
import { setDefaultOptions } from 'date-fns'
import { PropsWithChildren, useEffect, useMemo, useState } from 'react'
import { RawIntlProvider } from 'react-intl'
import { useSelector } from 'react-redux'

import { eventTracker } from '@masterplandev/utils'

import { getLocalStorageItem } from '@/core/utils/localStorage/getLocalStorageItem'
import { localeQueries } from '@/http/api/locale'
import { signupQueries } from '@/http/api/signup'
import { userLangSelector } from '@/user/selectors'

import { APP_LOCALE_FALLBACK } from '../constants'
import { updateIntl } from '../intl'
import { detectNavigatorLanguages } from '../utils/detectNavigatorLanguages'
import { getDateFnsLocale } from '../utils/getDateFnsLocale'
import { loadCjkFont } from '../utils/loadCjkFont'
import { resolveLocale } from '../utils/resolveLocale'

const localeImports = import.meta.glob<{ default: Record<string, string> }>(
  '/src/translations/*.json',
)

function queryParamLang() {
  return new URLSearchParams(window.location.search).get('lang')
}

function loadLocaleMessages(locale: string) {
  const loader =
    localeImports[`/src/translations/${locale}.json`] ??
    localeImports[`/src/translations/${APP_LOCALE_FALLBACK}.json`]

  if (!loader) {
    return Promise.reject(
      new Error(
        `No locale messages loader found for locale "${locale}" or fallback "${APP_LOCALE_FALLBACK}".`,
      ),
    )
  }

  return loader()
}

export default function LocaleProvider({ children }: PropsWithChildren) {
  const userLang = useSelector(userLangSelector)
  const { data } = useQuery(localeQueries.detect())
  // Read the invitee's chosen language from the signup-details cache when
  // present (populated by the signup flow); never fetch from here.
  const { data: signup } = useQuery({
    ...signupQueries.details(),
    enabled: false,
  })

  const localeToUse = resolveLocale([
    queryParamLang(),
    signup?.lang,
    getLocalStorageItem('locale'),
    userLang,
    data?.browser,
    detectNavigatorLanguages(window),
  ])

  const [messages, setMessages] = useState<Record<string, string>>({})

  useEffect(() => {
    const ip = data?.ip
    if (ip) {
      eventTracker.register({ ip })
      // @ts-expect-error TS(2554): Expected 1 arguments, but got 2.
      eventTracker.people.set('$ip', ip)
    }
  }, [data?.ip])

  useEffect(() => {
    document.documentElement.lang = localeToUse
    loadCjkFont(localeToUse)

    const dateLocale = getDateFnsLocale(localeToUse)
    setDefaultOptions({ locale: dateLocale })

    let cancelled = false
    loadLocaleMessages(localeToUse)
      .then((res) => {
        if (!cancelled) {
          setMessages(res.default)
        }
      })
      .catch((error) => {
        console.error('Failed to load locale messages', error)
      })

    return () => {
      cancelled = true
    }
  }, [localeToUse])

  // @ts-expect-error TS(2551): Property 'messages' does not exist on type 'Window'
  window.messages = messages

  // Intl instance shared with route head() titles via getIntl
  const intl = useMemo(
    () => updateIntl(localeToUse, messages),
    [localeToUse, messages],
  )

  return <RawIntlProvider value={intl}>{children}</RawIntlProvider>
}
